import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  CircularProgress,
  Switch,
  FormControlLabel,
  Paper,
  Autocomplete,
  Menu,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { getCustomerDetail, searchGuestByPhone } from '../../services/api';
import countriesData from '../../data/countries-states-cities-data';
import countryCodesData from '../../data/country-codes.json';
import { countryFilterOptions, stateFilterOptions, cityFilterOptions } from '../../utils/locationSearch';
import {
  formatAadhaarDisplay,
  isAadhaarDocumentType,
  normalizeDocumentIdInput,
  validateDocumentIdNumber,
  validateGuestDocumentImages,
} from '../../utils/documentIdFormatters';
import { captureNativeCameraDataUrl, getNativeCameraErrorMessage } from '../../utils/nativeCameraCapture';
import {
  formatPhoneLengthLabel,
  isValidPhoneForCountry,
  normalizePhoneInputForCountry,
  validatePhoneForCountry,
} from '../../utils/phoneValidation';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../../shared/constants/theme';
import { MOBILE_INPUT_FONT_SIZE } from '../../shared/theme/mobileInputZoomFix';
import { isMobileShellLayout } from '../../utils/mobileShellLayout';
import {
  createBookingDrawerOverlayProps,
  getBookingDrawerTheme,
  withDrawerMenuPaperSx,
} from '../../utils/bookingDrawerLayout';
import ForeignGuestDetailsDrawer from '../booking/ForeignGuestDetailsDrawer';
import {
  EMPTY_FOREIGN_GUEST_DETAILS,
  hasForeignGuestDetailsValues,
  isForeignGuestCountry,
  mapGuestApiToForeignDetails,
  resolveCountryCode,
  validateForeignGuestDetails,
} from '../../utils/foreignGuestFormUtils';
import PublicIcon from '@mui/icons-material/Public';
import EditIcon from '@mui/icons-material/Edit';

/**
 * Guest Details Step Component
 * Includes all guest information fields and document upload
 */
const GuestDetailsStep = ({
  // Name fields
  guestTitle,
  guestFirstName,
  guestMiddleName,
  guestLastName,
  onGuestTitleChange,
  onGuestFirstNameChange,
  onGuestMiddleNameChange,
  onGuestLastNameChange,
  onValidationChange, // Callback to notify parent of validation state
  onValidateRequest, // Callback to request validation (triggered when Next is clicked)
  // Contact fields
  guestEmail,
  guestPhone,
  guestPhoneCountryCode,
  onGuestEmailChange,
  onGuestPhoneChange,
  onGuestPhoneCountryCodeChange,
  onSelectedGuestIdChange,
  selectedGuestId,
  // Address fields
  guestAddress,
  guestCity,
  guestState,
  guestCountry,
  guestPincode,
  onGuestAddressChange,
  onGuestCityChange,
  onGuestStateChange,
  onGuestCountryChange,
  onGuestPincodeChange,
  // Personal fields
  guestDOB,
  guestGender,
  onGuestDOBChange,
  onGuestGenderChange,
  // Other fields
  guestNote,
  bookingReference,
  bookingSource,
  remarks,
  onGuestNoteChange,
  onBookingReferenceChange,
  onBookingSourceChange,
  uploadDocumentType,
  onUploadDocumentChange,
  documentIdNumber,
  onDocumentIdNumberChange,
  onRemarksChange,
  purposeOfStay,
  purposeOfStayNote,
  onPurposeOfStayChange,
  onPurposeOfStayNoteChange,
  // Document upload
  passportFrontId,
  passportBackId,
  onPassportFrontIdChange,
  onPassportBackIdChange,
  headingActions,
  /** When true, hide the "Guest Information" title; optional actions still render in a compact bar. */
  hideGuestInformationHeading = false,
  /** When true, keep Select/Menu/Autocomplete popovers inside the drawer (fixes mobile z-index). */
  inDrawer = false,
  isWalkIn = false,
  allowNoDocument = false,
  /** Hotel configured country (name or ISO code) — used to detect foreign guests. */
  hotelCountry = '',
  /** Room / stay context for foreign-guest compliance (not re-collected in the drawer). */
  foreignGuestRoomNumber = '',
  foreignGuestCheckInDate = null,
  foreignGuestDetails = EMPTY_FOREIGN_GUEST_DETAILS,
  onForeignGuestDetailsChange,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);
  const drawerOverlaySurface = isDark ? drawerTheme.surface : '#ffffff';
  const drawerOverlays = useMemo(
    () => (inDrawer ? createBookingDrawerOverlayProps(isDark, drawerOverlaySurface) : null),
    [inDrawer, isDark, drawerOverlaySurface]
  );
  const mobileFieldsSx = isMobileShellLayout()
    ? {
        '& .MuiInputBase-input, & .MuiSelect-select, & .MuiAutocomplete-input': {
          fontSize: `${MOBILE_INPUT_FONT_SIZE} !important`,
        },
      }
    : {};
  const [searching, setSearching] = useState(false);
  const [cameraUpload, setCameraUpload] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraType, setCameraType] = useState(null); // 'front' or 'back'
  const [stream, setStream] = useState(null);
  const [fullNameInput, setFullNameInput] = useState('');
  const [phoneCountryCodeMenuOpen, setPhoneCountryCodeMenuOpen] = useState(false);
  const [phoneCountryCodeSearch, setPhoneCountryCodeSearch] = useState('');
  const [titleMenuOpen, setTitleMenuOpen] = useState(false);
  const [guestSearchOptions, setGuestSearchOptions] = useState([]);
  const [phoneSearchDebounceTimer, setPhoneSearchDebounceTimer] = useState(null);
  const [cameraPermissionSnackbar, setCameraPermissionSnackbar] = useState({ open: false, message: '' });
  const [foreignGuestDrawerOpen, setForeignGuestDrawerOpen] = useState(false);
  const isForeignGuest = useMemo(
    () => isForeignGuestCountry(guestCountry, hotelCountry),
    [guestCountry, hotelCountry]
  );
  const foreignGuestComplete = useMemo(() => {
    if (!isForeignGuest) return true;
    return validateForeignGuestDetails(foreignGuestDetails).ok;
  }, [isForeignGuest, foreignGuestDetails]);
  const phoneCountryCodeAnchorRef = useRef(null);
  const titleMenuAnchorRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputFrontRef = useRef(null);
  const fileInputBackRef = useRef(null);
  const validateAllFieldsRef = useRef(null);
  
  // Validation errors
  const [errors, setErrors] = useState({});
  // Shared sentinel value for flows that allow "No Document".
  const WALK_IN_NO_DOCUMENT_VALUE = 'no_document';
  // Reservation and walk-in both can opt into "No Document" behavior.
  const noDocumentEnabled = isWalkIn || allowNoDocument;
  const effectiveDocumentType = (
    noDocumentEnabled && uploadDocumentType === WALK_IN_NO_DOCUMENT_VALUE
      ? ''
      : (uploadDocumentType || '')
  ).trim();
  const shouldShowDocumentNumberField = !(noDocumentEnabled && uploadDocumentType === WALK_IN_NO_DOCUMENT_VALUE);

  const hasDocumentImage = useCallback((value) => {
    if (!value || typeof value !== 'string') return false;
    const trimmed = value.trim();
    return (
      trimmed.startsWith('data:image') ||
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://')
    );
  }, []);

  const hasRequiredDocumentUpload =
    hasDocumentImage(passportFrontId) || hasDocumentImage(passportBackId);

  // Validate all required fields
  const isFormValid = useMemo(() => {
    const hasName = !!guestFirstName && guestFirstName.trim().length > 0;
    const hasCountry = !!guestCountry && guestCountry.trim().length > 0;
    const hasPurpose = !!purposeOfStay && String(purposeOfStay).trim().length > 0;
    const purposeOtherOk =
      purposeOfStay !== 'other' ||
      (!!purposeOfStayNote && String(purposeOfStayNote).trim().length > 0);

    const isPhoneValid = isValidPhoneForCountry(guestPhone, guestPhoneCountryCode);

    // Email validation: if email is provided, it must be valid
    const isEmailValid = !guestEmail || guestEmail.trim() === '' || !errors.email;

    // Document number + image: backend requires at least one side when type is selected
    const hasDocumentType = !!effectiveDocumentType;
    const hasDocumentIdNumber = !!documentIdNumber && documentIdNumber.trim().length > 0;
    const documentNumberError = validateDocumentIdNumber(effectiveDocumentType, documentIdNumber);
    const isDocumentNumberValid = !hasDocumentType || (hasDocumentIdNumber && !documentNumberError);
    const isDocumentUploadValid = !hasDocumentType || hasRequiredDocumentUpload;

    return hasName && isPhoneValid && hasCountry &&
           hasPurpose && purposeOtherOk &&
           isEmailValid && isDocumentNumberValid && isDocumentUploadValid &&
           foreignGuestComplete &&
           !errors.phone && !errors.firstName && !errors.email &&
           !errors.purposeOfStay && !errors.purposeOfStayNote && !errors.documentNumber &&
           !errors.documentUpload && !errors.foreignGuest;
  }, [guestFirstName, guestPhone, guestPhoneCountryCode, guestCountry, guestEmail,
      purposeOfStay, purposeOfStayNote,
      effectiveDocumentType, documentIdNumber, validateDocumentIdNumber,
      hasRequiredDocumentUpload, foreignGuestComplete,
      errors.phone, errors.firstName, errors.email,
      errors.purposeOfStay, errors.purposeOfStayNote, errors.documentNumber,
      errors.documentUpload, errors.foreignGuest]);
  
  // Notify parent component of validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isFormValid);
    }
  }, [isFormValid, onValidationChange]);
  
  // Validation functions
  const validateEmail = (email) => {
    if (!email) return '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? '' : 'Please enter a valid email address';
  };
  
  const validatePhone = (phone) => {
    return validatePhoneForCountry(phone, guestPhoneCountryCode);
  };

  // Store only the national number; max length follows the selected country code.
  const normalizePhoneInput = (value) => {
    return normalizePhoneInputForCountry(value, guestPhoneCountryCode);
  };
  
  const validatePincode = (pincode) => {
    if (!pincode) return '';
    const pincodeRegex = /^[A-Z0-9\s\-]{3,10}$/;
    return pincodeRegex.test(pincode) ? '' : 'Pincode must be 3-10 alphanumeric characters';
  };
  
  const validateName = (name) => {
    if (!name || !name.trim()) return 'This field is required';
    return '';
  };
  
  // Handle field changes with validation. Phone field stores the national number only.
  const handlePhoneChange = (value) => {
    const phoneOnly = normalizePhoneInput(value);
    onGuestPhoneChange(phoneOnly);
    const phoneError = validatePhone(phoneOnly);
    setErrors(prev => ({ ...prev, phone: phoneError }));
  };
  
  const handleEmailChange = (value) => {
    onGuestEmailChange(value);
    // Always validate email if provided - show error if invalid
    if (value && value.trim()) {
      const emailError = validateEmail(value);
      setErrors(prev => ({ ...prev, email: emailError }));
    } else {
      // Clear error if email is empty (email is optional)
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };
  
  const handleFirstNameChange = (value) => {
    onGuestFirstNameChange(value);
    // Show validation error immediately when user types
    const nameError = validateName(value);
    setErrors(prev => ({ ...prev, firstName: nameError }));
  };
  
  const handleLastNameChange = (value) => {
    onGuestLastNameChange(value);
    setErrors(prev => ({ ...prev, lastName: validateName(value) }));
  };
  
  const handlePincodeChange = (value) => {
    if (onGuestPincodeChange) {
      onGuestPincodeChange(value);
      if (value) {
        setErrors(prev => ({ ...prev, pincode: validatePincode(value) }));
      } else {
        setErrors(prev => ({ ...prev, pincode: '' }));
      }
    }
  };
  
  const handleCountryChange = (countryName) => {
    if (onGuestCountryChange) {
      onGuestCountryChange(countryName || '');
      const error = !countryName || !countryName.trim() ? 'Country is required' : '';
      setErrors(prev => ({ ...prev, country: error, foreignGuest: '' }));
    }
    if (hotelCountry && isForeignGuestCountry(countryName, hotelCountry)) {
      if (onUploadDocumentChange && uploadDocumentType !== 'passport') {
        onUploadDocumentChange('passport');
      }
      // Nationality must track the newly selected country (not prior guest nationality).
      if (onForeignGuestDetailsChange) {
        const nextCode = resolveCountryCode(countryName);
        const prevNationality = resolveCountryCode(foreignGuestDetails.nationality);
        const prevIssuing = resolveCountryCode(foreignGuestDetails.passportIssuingCountry);
        onForeignGuestDetailsChange({
          ...foreignGuestDetails,
          nationality: nextCode,
          passportIssuingCountry:
            !prevIssuing || prevIssuing === prevNationality ? nextCode : prevIssuing,
        });
      }
      setForeignGuestDrawerOpen(true);
    } else if (onForeignGuestDetailsChange && hasForeignGuestDetailsValues(foreignGuestDetails)) {
      // Keep prior details in memory; drawer only required while country is foreign.
      setForeignGuestDrawerOpen(false);
    }
  };
  
  const handleStateChange = (stateName) => {
    if (onGuestStateChange) {
      onGuestStateChange(stateName || '');
      setErrors(prev => ({ ...prev, state: '' }));
    }
  };

  const handleCityChange = (cityName) => {
    if (onGuestCityChange) {
      onGuestCityChange(cityName || '');
      setErrors(prev => ({ ...prev, city: '' }));
    }
  };


  const handleGenderChange = (gender) => {
    if (onGuestGenderChange) {
      onGuestGenderChange(gender || '');
      setErrors(prev => ({ ...prev, gender: '' }));
    }
  };

  // Function to validate all fields and show errors
  const MIN_PRIMARY_GUEST_AGE = 18;
  const maxPrimaryGuestDob = useMemo(
    () => moment().subtract(MIN_PRIMARY_GUEST_AGE, 'years'),
    [],
  );

  const getDobValidationError = useCallback((dateOrValue) => {
    if (dateOrValue == null || dateOrValue === '') return '';
    const date = moment.isMoment(dateOrValue)
      ? dateOrValue
      : moment(dateOrValue, ['YYYY-MM-DD', moment.ISO_8601], true);
    if (!date.isValid()) return 'Enter a valid date of birth.';
    if (date.isAfter(moment(), 'day')) return 'Date of birth cannot be in the future.';
    if (moment().diff(date, 'years') < MIN_PRIMARY_GUEST_AGE) {
      return 'Guest must be at least 18 years old.';
    }
    return '';
  }, []);

  const validateAllFields = useCallback(() => {
    const newErrors = {};
    
    // Validate name
    if (!guestFirstName || !guestFirstName.trim()) {
      newErrors.firstName = 'Name is required';
    }
    
    // Validate phone
    if (!guestPhone || !guestPhone.trim()) {
      newErrors.phone = 'Mobile number is required';
    } else {
      const phoneError = validatePhone(guestPhone);
      if (phoneError) {
        newErrors.phone = phoneError;
      } else if (
        guestSearchOptions.length > 0
        && !selectedGuestId
      ) {
        newErrors.phone = 'A customer with this mobile number already exists. Please select them from the search results.';
      }
    }
    
    // Validate country
    if (!guestCountry || !guestCountry.trim()) {
      newErrors.country = 'Country is required';
    }

    // Purpose of stay is required for walk-in / quick book only (hidden on reservation).
    if (isWalkIn) {
      if (!purposeOfStay || !String(purposeOfStay).trim()) {
        newErrors.purposeOfStay = 'Purpose of stay is required';
      }
      if (purposeOfStay === 'other') {
        if (!purposeOfStayNote || !String(purposeOfStayNote).trim()) {
          newErrors.purposeOfStayNote = 'Please describe the purpose when Other is selected';
        }
      }
    }
    
    // Validate email (if provided)
    if (guestEmail && guestEmail.trim()) {
      const emailError = validateEmail(guestEmail);
      if (emailError) {
        newErrors.email = emailError;
      }
    }

    // Primary guest DOB must be 18+ when provided (typed, pasted, OCR, or search fill).
    if (guestDOB && String(guestDOB).trim()) {
      const dobError = getDobValidationError(guestDOB);
      if (dobError) {
        newErrors.dob = dobError;
      }
    }
    
    // Validate document number + upload when document type is selected
    if (effectiveDocumentType) {
      const idNumberError = validateDocumentIdNumber(effectiveDocumentType, documentIdNumber);
      if (idNumberError) {
        newErrors.documentNumber = idNumberError;
      }
      if (!hasRequiredDocumentUpload) {
        newErrors.documentUpload =
          'At least one document (front or back) is required when document type is selected';
      }
    }

    if (isForeignGuest && !foreignGuestComplete) {
      newErrors.foreignGuest = 'Foreign guest details are required';
      setForeignGuestDrawerOpen(true);
    }

    setErrors((prev) => ({
      ...prev,
      ...newErrors,
      // Clear stale image errors when at least one side is present (or no document type).
      passportFront: newErrors.passportFront || '',
      passportBack: newErrors.passportBack || '',
    }));
    return Object.keys(newErrors).length === 0;
  }, [guestFirstName, guestPhone, guestPhoneCountryCode, guestCountry,
      guestEmail, guestDOB, purposeOfStay, purposeOfStayNote, effectiveDocumentType, documentIdNumber,
      hasRequiredDocumentUpload, isForeignGuest, foreignGuestComplete,
      validateDocumentIdNumber, getDobValidationError, isWalkIn,
      guestSearchOptions, selectedGuestId]);

  // Keep inline DOB error in sync when value is set from search/OCR/parent.
  useEffect(() => {
    if (!guestDOB || !String(guestDOB).trim()) return;
    const dobError = getDobValidationError(guestDOB);
    if (!dobError) {
      setErrors((prev) => (prev.dob ? { ...prev, dob: '' } : prev));
      return;
    }
    setErrors((prev) => (prev.dob === dobError ? prev : { ...prev, dob: dobError }));
    // Block under-18 values from sticking after autofill.
    if (
      onGuestDOBChange
      && moment(guestDOB).isValid()
      && moment().diff(moment(guestDOB), 'years') < MIN_PRIMARY_GUEST_AGE
    ) {
      onGuestDOBChange('');
    }
  }, [guestDOB, getDobValidationError, onGuestDOBChange]);

  // Purpose of stay is walk-in / quick book only — clear any prefilled value on reservation.
  useEffect(() => {
    if (!isWalkIn) {
      if (purposeOfStay && onPurposeOfStayChange) onPurposeOfStayChange('');
      if (purposeOfStayNote && onPurposeOfStayNoteChange) onPurposeOfStayNoteChange('');
    }
  }, [isWalkIn, purposeOfStay, purposeOfStayNote, onPurposeOfStayChange, onPurposeOfStayNoteChange]);

  // Clear document-upload error once a valid image is present (e.g. guest lookup)
  useEffect(() => {
    if (hasRequiredDocumentUpload) {
      setErrors((prev) => (prev.documentUpload ? { ...prev, documentUpload: '' } : prev));
    }
  }, [hasRequiredDocumentUpload]);

  // Store validation function in ref
  useEffect(() => {
    validateAllFieldsRef.current = validateAllFields;
  }, [validateAllFields]);

  // Expose validateAllFields to parent via onValidateRequest
  // Use useEffect to ensure it runs after render, not during
  useEffect(() => {
    if (onValidateRequest) {
      const validationWrapper = () => {
        // Always use the current ref value
        return validateAllFieldsRef.current ? validateAllFieldsRef.current() : false;
      };
      // useEffect runs after render, so calling state setter here is safe
      onValidateRequest(validationWrapper);
    }
    // Only depend on onValidateRequest, not validateAllFields
    // The ref will always have the latest function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onValidateRequest]);
  
  // Parse full name and split into first, middle, and last name
  // Handles spaces correctly - multiple spaces are treated as single space
  const parseFullName = (fullName) => {
    if (!fullName || !fullName.trim()) {
      return { firstName: '', middleName: '', lastName: '' };
    }
    
    // Trim and normalize multiple spaces to single space, then split
    const normalizedName = fullName.trim().replace(/\s+/g, ' ');
    const nameParts = normalizedName.split(' ').filter(part => part.length > 0);
    
    if (nameParts.length === 0) {
      return { firstName: '', middleName: '', lastName: '' };
    } else if (nameParts.length === 1) {
      // Single word - put in first name (spaces are preserved in the word itself)
      return { firstName: nameParts[0], middleName: '', lastName: '' };
    } else if (nameParts.length === 2) {
      // Two words: first = first name, second = last name
      return { firstName: nameParts[0], middleName: '', lastName: nameParts[1] };
    } else if (nameParts.length === 3) {
      // Three words: first = first name, second = middle name, third = last name
      return { firstName: nameParts[0], middleName: nameParts[1], lastName: nameParts[2] };
    } else {
      // More than 3 words: all except last 2 = first name, second last = middle name, last = last name
      // Join with spaces to preserve spacing in first name
      const firstName = nameParts.slice(0, nameParts.length - 2).join(' ');
      const middleName = nameParts[nameParts.length - 2];
      const lastName = nameParts[nameParts.length - 1];
      return { firstName, middleName, lastName };
    }
  };
  
  // Handle full name input change
  const handleFullNameChange = (fullName) => {
    // Update local state immediately to allow typing spaces
    setFullNameInput(fullName);
    
    // Parse and update individual name fields
    const { firstName, middleName, lastName } = parseFullName(fullName);
    
    // Update individual name fields
    onGuestFirstNameChange(firstName);
    if (onGuestMiddleNameChange) {
      onGuestMiddleNameChange(middleName);
    }
    onGuestLastNameChange(lastName);
    
    // Validate: show error immediately when user types
    const nameError = validateName(firstName);
    setErrors(prev => ({
      ...prev,
      firstName: nameError,
      lastName: '', // Last name is optional for single word names
    }));
  };

  // Keep the combined "Name" field in sync when parent sets first/middle/last (AI check-in, profile load).
  useEffect(() => {
    const reconstructed = [guestFirstName, guestMiddleName, guestLastName]
      .filter(Boolean)
      .join(' ')
      .trim();

    setFullNameInput((prev) => {
      const pt = (prev || '').trim();
      if (!reconstructed) {
        if (!guestFirstName && !guestMiddleName && !guestLastName) return '';
        return pt;
      }
      if (!pt) return reconstructed;
      if (pt === reconstructed) return prev;
      const { firstName, middleName, lastName } = parseFullName(prev);
      const matchesParts =
        (firstName || '').trim() === (guestFirstName || '').trim() &&
        (middleName || '').trim() === (guestMiddleName || '').trim() &&
        (lastName || '').trim() === (guestLastName || '').trim();
      if (matchesParts) return prev;
      return reconstructed;
    });

    if (reconstructed) {
      setErrors((prev) => ({ ...prev, firstName: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parseFullName is stable logic; sync when props change
  }, [guestFirstName, guestMiddleName, guestLastName]);

  // Get full name from individual fields
  const getFullName = () => {
    // If user is typing, use the input value, otherwise reconstruct from fields
    if (fullNameInput !== '') {
      return fullNameInput;
    }
    const parts = [];
    if (guestFirstName) parts.push(guestFirstName);
    if (guestMiddleName) parts.push(guestMiddleName);
    if (guestLastName) parts.push(guestLastName);
    return parts.join(' ');
  };

  const CUSTOM_BORDER_COLOR = isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)';

  // Get all countries from JSON data
  const countries = useMemo(() => countriesData.countries.map(country => ({
    code: country.code,
    name: country.name,
  })), []);
  
  // Get country codes for phone numbers
  const countryCodes = useMemo(() => countryCodesData, []);
  
  // Find selected country code objects
  const selectedPhoneCountryCode = useMemo(() => {
    return countryCodes.find(cc => cc.dialCode === guestPhoneCountryCode) || countryCodes.find(cc => cc.code === 'IN');
  }, [guestPhoneCountryCode, countryCodes]);
  

  // Find selected country object
  const selectedCountryObj = useMemo(() => {
    if (!guestCountry) return null;
    return countriesData.countries.find(c => c.name === guestCountry || c.code === guestCountry);
  }, [guestCountry]);

  // Get states for selected country
  const availableStates = useMemo(() => {
    if (!selectedCountryObj) return [];
    return selectedCountryObj.states.map(state => ({
      code: state.code,
      name: state.name,
    }));
  }, [selectedCountryObj]);

  // Find selected state object
  const selectedStateObj = useMemo(() => {
    if (!selectedCountryObj || !guestState) return null;
    return selectedCountryObj.states.find(s => s.name === guestState || s.code === guestState);
  }, [selectedCountryObj, guestState]);

  // Get cities for selected state
  const availableCities = useMemo(() => {
    if (!selectedStateObj) return [];
    return selectedStateObj.cities;
  }, [selectedStateObj]);

  // Include current guestCity in options when it's set but not in list (e.g. when going back so Autocomplete shows the saved value)
  const cityOptions = useMemo(() => {
    const base = availableCities || [];
    const cityTrim = guestCity ? String(guestCity).trim() : '';
    if (cityTrim && !base.some((c) => String(c).trim() === cityTrim)) {
      return [...base, guestCity];
    }
    return base;
  }, [availableCities, guestCity]);

  /** Only treat country/state changes as user-driven — skip mount/remount (e.g. Guest → Review → Guest). */
  const prevGuestCountryForResetRef = useRef(undefined);
  const prevGuestStateForCityResetRef = useRef(undefined);

  // Reset state and city only when country actually changes (not on initial mount/remount).
  useEffect(() => {
    const prev = prevGuestCountryForResetRef.current;
    if (prev === undefined) {
      prevGuestCountryForResetRef.current = guestCountry;
      return;
    }
    if (prev === guestCountry) {
      return;
    }
    prevGuestCountryForResetRef.current = guestCountry;

    if (guestCountry && onGuestStateChange) {
      const currentState = selectedCountryObj?.states?.find(
        (s) => s.name === guestState || s.code === guestState
      );
      if (!currentState && guestState) {
        onGuestStateChange('');
        if (onGuestCityChange) onGuestCityChange('');
      }
    }
  }, [guestCountry]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset city only when state actually changes (not on initial mount/remount).
  useEffect(() => {
    const prev = prevGuestStateForCityResetRef.current;
    if (prev === undefined) {
      prevGuestStateForCityResetRef.current = guestState;
      return;
    }
    if (prev === guestState) {
      return;
    }
    prevGuestStateForCityResetRef.current = guestState;

    if (guestState && guestCity && onGuestCityChange) {
      const currentState = selectedCountryObj?.states?.find(
        (s) => s.name === guestState || s.code === guestState
      );
      if (currentState && currentState.cities?.length) {
        const cityInList = currentState.cities.some(
          (c) => String(c).trim().toLowerCase() === String(guestCity).trim().toLowerCase()
        );
        if (!cityInList) onGuestCityChange('');
      }
    }
  }, [guestState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search: populate dropdown only — never clear or autofill until user selects an option.
  const searchGuestDebounced = useCallback(async (phoneNumber) => {
    if (!phoneNumber || phoneNumber.trim().length < 3) {
      setGuestSearchOptions([]);
      return;
    }

    setSearching(true);
    try {
      const phoneToSearch = phoneNumber.trim().replace(/\D/g, '');

      if (phoneToSearch.length < 3) {
        setGuestSearchOptions([]);
        setSearching(false);
        return;
      }
      const response = await searchGuestByPhone(phoneToSearch);

      if (response.guest) {
        const guest = response.guest;
        const guestName = [
          guest.first_name || '',
          guest.middle_name || '',
          guest.last_name || ''
        ].filter(Boolean).join(' ') || 'Guest';
        setGuestSearchOptions([{
          id: guest.id,
          name: guestName,
          phone: guest.phone || phoneToSearch,
          email: guest.email || '',
          guest: guest
        }]);
      } else {
        setGuestSearchOptions([]);
      }
    } catch (error) {
      setGuestSearchOptions([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Auto-fill guest information from search result.
  // When forceOverwrite is true (e.g. after search/paste another number), always set from guest so new user replaces previous.
  const autoFillGuestInfo = useCallback((guest, forceOverwrite = false) => {
        if (guest.title && (forceOverwrite || !guestTitle) && onGuestTitleChange) {
          onGuestTitleChange(guest.title);
        }
        if (guest.first_name && (forceOverwrite || !guestFirstName)) {
          onGuestFirstNameChange(guest.first_name);
        }
        if (guest.middle_name && (forceOverwrite || !guestMiddleName) && onGuestMiddleNameChange) {
          onGuestMiddleNameChange(guest.middle_name);
        }
        if (guest.last_name && (forceOverwrite || !guestLastName)) {
          onGuestLastNameChange(guest.last_name);
        }
        // Update full name input when guest data is loaded from search
        const reconstructedName = [
          guest.first_name || guestFirstName,
          guest.middle_name || guestMiddleName,
          guest.last_name || guestLastName
        ].filter(Boolean).join(' ');
        if (reconstructedName) {
          setFullNameInput(reconstructedName);
        }
        if (guest.email && (forceOverwrite || !guestEmail)) {
          onGuestEmailChange(guest.email);
        }
        if (guest.address_line1 && (forceOverwrite || !guestAddress) && onGuestAddressChange) {
          onGuestAddressChange(guest.address_line1);
        }
        const guestDocumentType = String(guest.document_type || '').trim();
        const hasGuestDocumentType = guestDocumentType.length > 0;
        const hasGuestDocumentNumber = String(guest.id_number || '').trim().length > 0;
        const hasGuestDocumentFront = String(guest.id_front_url || '').trim().length > 0;
        const hasGuestDocumentBack = String(guest.id_back_url || '').trim().length > 0;

        // Load existing guest documents (always load if guest has documents).
        if (hasGuestDocumentType && onUploadDocumentChange && (forceOverwrite || !uploadDocumentType)) {
          // Use document_type exactly as stored in database
          let docType = guestDocumentType;
          // Check if the document type exists in the options, if not, try to find a match
          const docOption = documentUploadOptions.find(opt => opt.value === docType || opt.value.toLowerCase() === docType.toLowerCase());
          if (docOption) {
            // Use the exact value from options to ensure it matches
            docType = docOption.value;
          }
          // Set the document type - will update the dropdown
          onUploadDocumentChange(docType);
        } else if (
          noDocumentEnabled &&
          onUploadDocumentChange &&
          !hasGuestDocumentNumber &&
          !hasGuestDocumentFront &&
          !hasGuestDocumentBack
        ) {
          // Existing guest without any saved document should auto-select "No Document" in enabled flows.
          onUploadDocumentChange(WALK_IN_NO_DOCUMENT_VALUE);
          if (onDocumentIdNumberChange) onDocumentIdNumberChange('');
          if (onPassportFrontIdChange) onPassportFrontIdChange('');
          if (onPassportBackIdChange) onPassportBackIdChange('');
        }
        if (guest.id_number && onDocumentIdNumberChange && (forceOverwrite || !documentIdNumber)) {
          onDocumentIdNumberChange(guest.id_number);
        }
        if (guest.id_front_url && onPassportFrontIdChange && (forceOverwrite || !passportFrontId)) {
          onPassportFrontIdChange(guest.id_front_url);
        }
        if (guest.id_back_url && onPassportBackIdChange && (forceOverwrite || !passportBackId)) {
          onPassportBackIdChange(guest.id_back_url);
        }
        if (onForeignGuestDetailsChange && (forceOverwrite || !hasForeignGuestDetailsValues(foreignGuestDetails))) {
          // Prefer guest.country (selected residence country) for nationality — not stale nationality.
          onForeignGuestDetailsChange(mapGuestApiToForeignDetails(guest, guest.country || guestCountry));
        }
        // Map country code to country name
        if (guest.country && onGuestCountryChange && (forceOverwrite || !guestCountry)) {
          // Backend returns country code (e.g., "IN"), frontend needs country name (e.g., "India")
          const countryObj = countriesData.countries.find(c => c.code === guest.country);
          let countryName = guest.country;
          if (countryObj) {
            countryName = countryObj.name;
          } else {
            // Fallback: try to find by name if code not found
            const countryByName = countriesData.countries.find(c => c.name === guest.country);
            if (countryByName) {
              countryName = countryByName.name;
            }
          }
          
          // Set country first
          onGuestCountryChange(countryName);
          
          // After country is set, set state and city (with delay to ensure dropdowns update)
          setTimeout(() => {
            if (guest.state && onGuestStateChange) {
              onGuestStateChange(guest.state);
              
              // After state is set, set city
              setTimeout(() => {
                if (guest.city && onGuestCityChange) {
                  onGuestCityChange(guest.city);
                }
              }, 150);
            } else if (guest.city && onGuestCityChange) {
              // If no state but city exists, set city after country
              setTimeout(() => {
                onGuestCityChange(guest.city);
              }, 150);
            }
          }, 150);
        } else {
          // If no country in search results, still try to set state and city
          if (guest.state && onGuestStateChange) {
            onGuestStateChange(guest.state);
            setTimeout(() => {
              if (guest.city && onGuestCityChange) {
                onGuestCityChange(guest.city);
              }
            }, 100);
          } else if (guest.city && onGuestCityChange) {
            onGuestCityChange(guest.city);
          }
        }
        if (guest.pincode && (forceOverwrite || !guestPincode) && onGuestPincodeChange) {
          onGuestPincodeChange(guest.pincode);
        }
        if (guest.dob && (forceOverwrite || !guestDOB) && onGuestDOBChange) {
          const dobError = getDobValidationError(guest.dob);
          if (dobError) {
            setErrors((prev) => ({ ...prev, dob: dobError }));
            onGuestDOBChange('');
          } else {
            onGuestDOBChange(guest.dob);
            setErrors((prev) => ({ ...prev, dob: '' }));
          }
        }
        if (guest.gender && (forceOverwrite || !guestGender) && onGuestGenderChange) {
          onGuestGenderChange(guest.gender);
        }
  }, [guestTitle, guestFirstName, guestMiddleName, guestLastName, guestEmail, guestAddress, guestCountry, guestState, guestCity, guestPincode, guestDOB, guestGender, uploadDocumentType, documentIdNumber, passportFrontId, passportBackId, onGuestTitleChange, onGuestFirstNameChange, onGuestMiddleNameChange, onGuestLastNameChange, onGuestEmailChange, onGuestAddressChange, onGuestCountryChange, onGuestStateChange, onGuestCityChange, onGuestPincodeChange, onGuestDOBChange, onGuestGenderChange, onUploadDocumentChange, onDocumentIdNumberChange, onPassportFrontIdChange, onPassportBackIdChange, setFullNameInput, documentUploadOptions, noDocumentEnabled, getDobValidationError]);

  // Handle phone number change: search API for dropdown options only (no autofill until selection).
  const handlePhoneChangeWithSearch = (value) => {
    handlePhoneChange(value);
    if (onSelectedGuestIdChange) {
      onSelectedGuestIdChange(null);
    }

    if (phoneSearchDebounceTimer) {
      clearTimeout(phoneSearchDebounceTimer);
      setPhoneSearchDebounceTimer(null);
    }

    const digitsOnly = (value || '').replace(/\D/g, '');
    if (digitsOnly.length >= 3) {
      const timer = setTimeout(
        () => searchGuestDebounced(value),
        isValidPhoneForCountry(digitsOnly, guestPhoneCountryCode) ? 0 : 400
      );
      setPhoneSearchDebounceTimer(timer);
    } else {
      setGuestSearchOptions([]);
    }
  };

  // Handle guest selection from autocomplete — only then load profile into the form.
  const handleGuestSelect = async (event, value, reason) => {
    if (reason === 'clear' || !value) {
      handlePhoneChangeWithSearch('');
      setGuestSearchOptions([]);
      if (onSelectedGuestIdChange) {
        onSelectedGuestIdChange(null);
      }
      return;
    }
    let option = value && typeof value === 'object' && value.guest ? value : null;
    if (!option && typeof value === 'string') {
      const needle = value.trim().toLowerCase();
      const digits = needle.replace(/\D/g, '');
      option = guestSearchOptions.find((opt) => {
        const optPhone = String(opt.phone || '').replace(/\D/g, '');
        const optName = String(opt.name || '').toLowerCase();
        return (digits && optPhone && (optPhone === digits || optPhone.endsWith(digits)))
          || optName === needle;
      }) || null;
    }
    if (!option?.guest) {
      return;
    }
    let guest = option.guest;
    if (guest.id) {
      try {
        const customerDetailResponse = await getCustomerDetail(guest.id);
        if (customerDetailResponse && customerDetailResponse.customer) {
          const fullGuest = customerDetailResponse.customer;
          guest = {
            ...guest,
            ...fullGuest,
            id_front_url: guest.id_front_url || fullGuest.id_front_url || null,
            id_back_url: guest.id_back_url || fullGuest.id_back_url || null,
            document_type: guest.document_type || fullGuest.document_type || null,
          };
        }
      } catch (err) {
        // Search payload already has stay-relevant fields; keep that if detail fails.
      }
    }
    if (onSelectedGuestIdChange) {
      onSelectedGuestIdChange(guest.id || null);
    }
    autoFillGuestInfo(guest, true);
    const rawPhoneDigits = option.phone ? String(option.phone).replace(/\D/g, '') : '';
    const guestDialCode = guest.country_code ? String(guest.country_code).replace(/\D/g, '') : '';
    const phoneNational = (guestDialCode && rawPhoneDigits.startsWith(guestDialCode))
      ? rawPhoneDigits.slice(guestDialCode.length) : rawPhoneDigits;
    if (guest.country_code && onGuestPhoneCountryCodeChange) {
      const code = guest.country_code.startsWith('+') ? guest.country_code : `+${guest.country_code}`;
      const validDialCode = countryCodes.find(cc => cc.dialCode === code);
      onGuestPhoneCountryCodeChange(validDialCode ? validDialCode.dialCode : '+91');
    }
    if (onGuestPhoneChange) {
      onGuestPhoneChange(phoneNational);
    }
    setGuestSearchOptions([]);
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (phoneSearchDebounceTimer) {
        clearTimeout(phoneSearchDebounceTimer);
      }
    };
  }, [phoneSearchDebounceTimer]);

  const clearDocumentImageErrors = () => {
    setErrors((prev) =>
      prev.passportFront || prev.passportBack
        ? { ...prev, passportFront: '', passportBack: '' }
        : prev,
    );
  };

  const syncDocumentImageErrors = (idFront, idBack) => {
    const imageErrors = validateGuestDocumentImages(effectiveDocumentType, idFront, idBack);
    setErrors((prev) => ({
      ...prev,
      passportFront: imageErrors.id_front,
      passportBack: imageErrors.id_back,
    }));
  };

  const handleFileUpload = (e, type) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageData = reader.result;
        if (type === 'front') {
          onPassportFrontIdChange(imageData);
        } else {
          onPassportBackIdChange(imageData);
        }
        clearDocumentImageErrors();
        setErrors((prev) => ({ ...prev, documentUpload: '' }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle document removal
  const handleDocumentRemove = (type) => {
    if (type === 'front') {
      onPassportFrontIdChange('');
      syncDocumentImageErrors('', passportBackId);
    } else {
      onPassportBackIdChange('');
      syncDocumentImageErrors(passportFrontId, '');
    }
  };

  const showCameraPermissionMessage = (message) => {
    setCameraPermissionSnackbar({
      open: true,
      message:
        message ||
        'Camera access was denied or is unavailable. Allow camera permission in your browser settings for this site, or turn off Camera upload to choose a file.',
    });
  };

  const startCamera = async (type) => {
    try {
      const nativeImageData = await captureNativeCameraDataUrl();
      if (nativeImageData) {
        if (type === 'front') {
          onPassportFrontIdChange(nativeImageData);
        } else {
          onPassportBackIdChange(nativeImageData);
        }
        clearDocumentImageErrors();
        setErrors((prev) => ({ ...prev, documentUpload: '' }));
        return;
      }
    } catch (error) {
      const message = getNativeCameraErrorMessage(error);
      if (message) showCameraPermissionMessage(message);
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      showCameraPermissionMessage(
        'Camera is not available in this browser or page context. Turn off Camera upload to upload a file instead.'
      );
      return;
    }
    try {
      // Stop any existing stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      // Request camera access
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use back camera by default
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      setStream(mediaStream);
      setCameraType(type);
      setCameraOpen(true);

      // Wait a bit for the modal to render, then set video source
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(err => {});
        }
      }, 100);
    } catch (error) {
      // Camera mode is on — do not open the file picker; prompt for permission instead
      showCameraPermissionMessage();
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraOpen(false);
    setCameraType(null);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      context.drawImage(video, 0, 0);

      // Convert canvas to base64 image
      const imageData = canvas.toDataURL('image/jpeg', 0.8);

      // Save the image
      if (cameraType === 'front') {
        onPassportFrontIdChange(imageData);
      } else {
        onPassportBackIdChange(imageData);
      }
      clearDocumentImageErrors();
      setErrors((prev) => ({ ...prev, documentUpload: '' }));

      // Stop camera
      stopCamera();
    }
  };

  const handleUploadClick = (type) => {
    if (cameraUpload) {
      // Camera upload enabled - open camera
      startCamera(type);
    } else {
      // Camera upload disabled - open file picker
      if (type === 'front') {
        fileInputFrontRef.current?.click();
      } else {
        fileInputBackRef.current?.click();
      }
    }
  };

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Booking source options
  const bookingSources = [
    { value: 'direct', label: 'Direct (Roomnexa)' },
    { value: 'booking_engine', label: 'Booking Engine (NexaStay)' },
    { value: 'ota', label: 'OTA (Online Travel Agency)' },
    { value: 'walk_in', label: 'Walk-In' },
    { value: 'corporate', label: 'Corporate' },
    { value: 'group', label: 'Group Booking' },
    { value: 'agent', label: 'Travel Agent' },
    { value: 'api', label: 'API Integration' },
    { value: 'other', label: 'Other' },
  ];

  // Booking source options
  const documentUploadOptions = [
    { value: 'aadhaar', label: 'Aadhaar Card' },
    { value: 'passport', label: 'Passport' },
    { value: 'voter_id', label: 'Voter ID (EPIC)' },
    { value: 'driving_license', label: 'Driving Licence' },
    { value: 'pan', label: 'PAN Card' },
    { value: 'government_id', label: 'Government Issued ID' },
    { value: 'oci', label: 'OCI Card (Overseas Citizen of India)' },
    { value: 'pio', label: 'PIO Card' },
    { value: 'student_id', label: 'Student ID (Govt/Recognised Institution)' },
    { value: 'company_id', label: 'Corporate / Company ID' },
    { value: 'other', label: 'Other Document' },
  ];
  const documentTypeOptions = noDocumentEnabled
    ? [...documentUploadOptions, { value: WALK_IN_NO_DOCUMENT_VALUE, label: 'No Document' }]
    : documentUploadOptions;

  const purposeOfStayOptions = [
    { value: 'business', label: 'Business' },
    { value: 'leisure', label: 'Leisure' },
    { value: 'medical', label: 'Medical' },
    { value: 'family_visit', label: 'Family Visit' },
    { value: 'event', label: 'Event' },
    // Transit is walk-in / quick-book only (not shown on reservation create).
    ...(isWalkIn ? [{ value: 'transit', label: 'Transit' }] : []),
    { value: 'education', label: 'Education' },
    { value: 'government', label: 'Government' },
    { value: 'other', label: 'Other' },
  ];

  const handlePurposeChipClick = (value) => {
    if (onPurposeOfStayChange) {
      onPurposeOfStayChange(value);
    }
    if (value !== 'other' && onPurposeOfStayNoteChange) {
      onPurposeOfStayNoteChange('');
    }
    setErrors((prev) => ({
      ...prev,
      purposeOfStay: '',
      ...(value !== 'other' ? { purposeOfStayNote: '' } : {}),
    }));
  };
  
  // Get selected document type label
  const getDocumentTypeLabel = () => {
    if (!effectiveDocumentType) return 'Document';
    const docType = documentUploadOptions.find(opt => opt.value === effectiveDocumentType);
    return docType ? docType.label : 'Document';
  };

  const getDocumentNumberPlaceholder = () => {
    if (effectiveDocumentType === 'aadhaar') return 'XXXX-XXXX-XXXX';
    if (effectiveDocumentType === 'passport') return 'A1234567';
    if (effectiveDocumentType === 'pan') return 'ABCDE1234F';
    if (effectiveDocumentType === 'voter_id') return 'ABC1234567';
    return 'Enter ID number';
  };

  const isAadhaarSelected = isAadhaarDocumentType(effectiveDocumentType);
  const normalizedDocumentIdValue = isAadhaarSelected
    ? formatAadhaarDisplay(documentIdNumber)
    : (documentIdNumber || '');
  
  // Get document upload heading
  const getDocumentUploadHeading = () => {
    const docLabel = getDocumentTypeLabel();
    return `${docLabel} Upload`;
  };
  
  // Get front document label
  const getFrontDocumentLabel = () => {
    const docLabel = getDocumentTypeLabel();
    return `Upload ${docLabel} Front`;
  };
  
  // Get back document label
  const getBackDocumentLabel = () => {
    const docLabel = getDocumentTypeLabel();
    return `Upload ${docLabel} Back`;
  };
  // Title options
  const titleOptions = [
    { value: 'Mr', label: 'Mr' },
    { value: 'Mrs', label: 'Mrs' },
    { value: 'Ms', label: 'Ms' },
    { value: 'Dr', label: 'Dr' },
    { value: 'Prof', label: 'Prof' },
    { value: 'Sir', label: 'Sir' },
    { value: 'Madam', label: 'Madam' },
  ];

  // Gender options
  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
    <Box sx={{ ...mobileFieldsSx, ...(drawerOverlays?.fieldShellSx ?? {}) }}>
      {hideGuestInformationHeading ? (
        headingActions ? (
          <Box sx={{ width: '100%', mb: 2 }}>
            {headingActions}
          </Box>
        ) : null
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: 1.5,
            mb: 3,
            rowGap: 1.5,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
              fontWeight: 500,
              minWidth: 0,
              flex: '1 1 auto',
            }}
          >
            Guest Information
          </Typography>
          {headingActions ? (
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: { xs: 0, sm: 2 } }}>
              {headingActions}
            </Box>
          ) : null}
        </Box>
      )}
        <Grid container spacing={2} sx={{ mt: hideGuestInformationHeading ? 0 : 1 }}>
          {/* Row 1: Mobile Number (with integrated country code and autocomplete) and Name fields */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              freeSolo
              options={guestSearchOptions}
              slotProps={drawerOverlays?.autocompleteSlotProps}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.phone || '';
              }}
              inputValue={guestPhone || ''}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'selectOption') {
                  handleGuestSelect(event, newInputValue, reason);
                  return;
                }
                if (reason === 'input' || reason === 'clear') {
                  const nationalOnly = normalizePhoneInput(newInputValue);
                  handlePhoneChangeWithSearch(nationalOnly);
                }
              }}
              onChange={handleGuestSelect}
              onBlur={() => {
                // Show error on blur if field is empty or invalid
                if (!guestPhone || !guestPhone.trim()) {
                  setErrors(prev => ({ ...prev, phone: 'Mobile number is required' }));
                } else {
                  const phoneError = validatePhone(guestPhone);
                  setErrors(prev => ({ ...prev, phone: phoneError }));
                }
              }}
              loading={searching}
              value={null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="mobile"
                  label="Mobile Number"
                  required
                  error={!!errors.phone}
                  helperText={errors.phone || (guestSearchOptions.length > 0 && !selectedGuestId
                    ? 'A saved guest matches this number. Select them to continue.'
                    : `Enter ${formatPhoneLengthLabel(guestPhoneCountryCode)}; if a saved guest appears, select them to load details`)}
                  inputProps={{
                    ...params.inputProps,
                    maxLength: normalizePhoneInputForCountry('9'.repeat(20), guestPhoneCountryCode).length,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, '');
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Box
                            ref={phoneCountryCodeAnchorRef}
                            onClick={() => setPhoneCountryCodeMenuOpen(true)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              gap: 0.5,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                              },
                            }}
                          >
                            <Typography component="span" sx={{ fontSize: '1.2rem' }}>
                              {selectedPhoneCountryCode?.flag || '🇮🇳'}
                            </Typography>
                            <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                              {selectedPhoneCountryCode?.dialCode || '+91'}
                            </Typography>
                            <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              ▼
                            </Typography>
                          </Box>
                        </InputAdornment>
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: errors.phone ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                      '&:hover fieldset': {
                        borderColor: errors.phone ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: errors.phone ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {option.phone} {option.email ? `• ${option.email}` : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
            <Menu
              anchorEl={phoneCountryCodeAnchorRef.current}
              open={phoneCountryCodeMenuOpen}
              onClose={() => {
                setPhoneCountryCodeMenuOpen(false);
                setPhoneCountryCodeSearch('');
              }}
              {...withDrawerMenuPaperSx(drawerOverlays?.menuProps, {
                maxHeight: 400,
                width: 300,
              })}
            >
              <Box sx={{ p: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search country..."
                  value={phoneCountryCodeSearch}
                  onChange={(e) => setPhoneCountryCodeSearch(e.target.value)}
                  sx={{ mb: 1 }}
                />
              </Box>
              {countryCodes
                .filter((country) => {
                  if (!phoneCountryCodeSearch) return true;
                  const search = phoneCountryCodeSearch.toLowerCase();
                  return (
                    country.name.toLowerCase().includes(search) ||
                    country.dialCode.includes(search) ||
                    country.code.toLowerCase().includes(search)
                  );
                })
                .map((country) => (
                <MenuItem
                  key={country.code}
                  onClick={() => {
                    if (onSelectedGuestIdChange) {
                      onSelectedGuestIdChange(null);
                    }
                    const normalizedPhone = normalizePhoneInputForCountry(guestPhone, country.dialCode);
                    if (onGuestPhoneCountryCodeChange) {
                      onGuestPhoneCountryCodeChange(country.dialCode);
                    }
                    if (normalizedPhone !== guestPhone && onGuestPhoneChange) {
                      onGuestPhoneChange(normalizedPhone);
                    }
                    setErrors(prev => ({
                      ...prev,
                      phone: validatePhoneForCountry(normalizedPhone, country.dialCode),
                    }));
                    setPhoneCountryCodeMenuOpen(false);
                    setPhoneCountryCodeSearch('');
                  }}
                  selected={selectedPhoneCountryCode?.dialCode === country.dialCode}
                >
                  <ListItemIcon>
                    <Typography sx={{ fontSize: '1.2rem' }}>{country.flag}</Typography>
                  </ListItemIcon>
                  <ListItemText
                    primary={country.dialCode}
                    secondary={country.name}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </MenuItem>
              ))}
            </Menu>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Name"
              value={fullNameInput}
              onChange={(e) => handleFullNameChange(e.target.value)}
              onBlur={() => {
                // Show error on blur if field is empty
                if (!fullNameInput || !fullNameInput.trim()) {
                  setErrors(prev => ({ ...prev, firstName: 'Name is required' }));
                }
              }}
              required
              error={!!errors.firstName}
              helperText={errors.firstName || 'Enter full name or first name'}
              placeholder="Name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      ref={titleMenuAnchorRef}
                      onClick={() => setTitleMenuOpen(true)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                        },
                      }}
                    >
                      <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.875rem', minWidth: 40 }}>
                        {guestTitle || 'Title'}
                      </Typography>
                      <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        ▼
                      </Typography>
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: errors.firstName ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                  },
                  '&:hover fieldset': {
                    borderColor: errors.firstName ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: errors.firstName ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                  },
                },
              }}
          />
          <Menu
            anchorEl={titleMenuAnchorRef.current}
            open={titleMenuOpen}
            onClose={() => setTitleMenuOpen(false)}
            {...withDrawerMenuPaperSx(drawerOverlays?.menuProps, {
              maxHeight: 300,
              minWidth: 150,
            })}
          >
            <MenuItem
              onClick={() => {
                if (onGuestTitleChange) {
                  onGuestTitleChange('');
                }
                setTitleMenuOpen(false);
              }}
            >
              <em>None</em>
            </MenuItem>
            {titleOptions.map((title) => (
              <MenuItem
                key={title.value}
                onClick={() => {
                  if (onGuestTitleChange) {
                    onGuestTitleChange(title.value);
                  }
                  setTitleMenuOpen(false);
                }}
                selected={guestTitle === title.value}
              >
                {title.label}
              </MenuItem>
            ))}
          </Menu>
        </Grid>
        <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={guestEmail || ''}
              onChange={(e) => handleEmailChange(e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              inputProps={{
                autoComplete: 'email',
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: errors.email ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                  },
                  '&:hover fieldset': {
                    borderColor: errors.email ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: errors.email ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                  },
                },
              }}
          />
        </Grid>

          {/* Row 3: Gender and Date of Birth */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth error={!!errors.gender}>
              <InputLabel>Gender</InputLabel>
              <Select
                value={guestGender || ''}
                label="Gender"
                onChange={(e) => handleGenderChange(e.target.value)}
                MenuProps={drawerOverlays?.selectMenuProps}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: errors.gender ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: errors.gender ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: errors.gender ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select Gender</em>
                </MenuItem>
                {genderOptions.map((gender) => (
                  <MenuItem key={gender.value} value={gender.value}>
                    {gender.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.gender && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.gender}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <DatePicker
              label="Date of Birth"
              value={guestDOB ? moment(guestDOB) : null}
              onChange={(date) => {
                if (!onGuestDOBChange) return;
                if (date && date.isValid()) {
                  const dobError = getDobValidationError(date);
                  if (dobError) {
                    // Block under-18 / future DOB — keep field empty and show inline error.
                    onGuestDOBChange('');
                    setErrors((prev) => ({ ...prev, dob: dobError }));
                    return;
                  }
                  onGuestDOBChange(date.format('YYYY-MM-DD'));
                  setErrors((prev) => ({ ...prev, dob: '' }));
                  return;
                }
                onGuestDOBChange('');
                setErrors((prev) => ({ ...prev, dob: '' }));
              }}
              maxDate={maxPrimaryGuestDob}
              disableFuture
              slotProps={{
                ...drawerOverlays?.datePickerSlotProps,
                textField: {
                  fullWidth: true,
                  error: !!errors.dob,
                  helperText: errors.dob || 'Must be at least 18 years old',
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: errors.dob ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                      '&:hover fieldset': {
                        borderColor: errors.dob ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: errors.dob ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                    },
                  },
                },
              }}
            />
          </Grid>

          {/* Row 4: Address Line 1 */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Guest Address"
              value={guestAddress || ''}
            onChange={(e) => onGuestAddressChange(e.target.value)}
            multiline
            rows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: CUSTOM_BORDER_COLOR,
                  },
                  '&:hover fieldset': {
                    borderColor: CUSTOM_BORDER_COLOR,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: CUSTOM_BORDER_COLOR,
                  },
                },
              }}
          />
        </Grid>

          {/* Row 5: Country, State, City, Pincode */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={countries}
              getOptionLabel={(option) => option.name}
              filterOptions={countryFilterOptions}
              slotProps={drawerOverlays?.autocompleteSlotProps}
              value={countries.find(c => 
                c.name === guestCountry || 
                c.code === guestCountry ||
                c.name?.toLowerCase() === guestCountry?.toLowerCase()
              ) || null}
              onChange={(event, newValue) => {
                handleCountryChange(newValue ? newValue.name : '');
              }}
              onBlur={() => {
                // Show error on blur if country is not selected
                if (!guestCountry || !guestCountry.trim()) {
                  setErrors(prev => ({ ...prev, country: 'Country is required' }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Country"
                  required
                  error={!!errors.country || !!errors.foreignGuest}
                  helperText={
                    errors.foreignGuest ||
                    errors.country ||
                    (isForeignGuest
                      ? 'Foreign guest details required'
                      : 'Auto-selected from hotel')
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: (errors.country || errors.foreignGuest) ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                      '&:hover fieldset': {
                        borderColor: (errors.country || errors.foreignGuest) ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: (errors.country || errors.foreignGuest) ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                    },
                  }}
                />
              )}
            />
            {isForeignGuest ? (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  icon={<PublicIcon sx={{ fontSize: '16px !important' }} />}
                  label={
                    foreignGuestComplete
                      ? 'Foreign guest details saved'
                      : 'Add foreign guest details'
                  }
                  onClick={() => setForeignGuestDrawerOpen(true)}
                  color={foreignGuestComplete ? 'success' : 'warning'}
                  variant={foreignGuestComplete ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 700 }}
                />
                <IconButton
                  size="small"
                  onClick={() => setForeignGuestDrawerOpen(true)}
                  aria-label="Edit foreign guest details"
                  sx={{ color: THEME_COLORS.primary || '#4ce1b6' }}
                >
                  <EditIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            ) : null}
      </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={availableStates}
              getOptionLabel={(option) => option.name}
              filterOptions={stateFilterOptions}
              slotProps={drawerOverlays?.autocompleteSlotProps}
              value={
                availableStates.find(
                  (s) => s.name === guestState || s.code === guestState
                ) || null
              }
              onChange={(event, newValue) => {
                handleStateChange(newValue ? newValue.name : '');
              }}
              disabled={!selectedCountryObj}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  error={!!errors.state}
                  helperText={errors.state || (selectedCountryObj ? 'Select a state' : 'Select country first')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: errors.state ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                      '&:hover fieldset': {
                        borderColor: errors.state ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: errors.state ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={cityOptions}
              getOptionLabel={(option) => (option != null ? String(option) : '')}
              filterOptions={cityFilterOptions}
              slotProps={drawerOverlays?.autocompleteSlotProps}
              value={cityOptions.find((c) => String(c).trim() === String(guestCity || '').trim()) || (guestCity ? guestCity : null)}
              onChange={(event, newValue) => {
                handleCityChange(newValue != null ? String(newValue) : '');
              }}
              disabled={!selectedStateObj}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  error={!!errors.city}
                  helperText={errors.city || (selectedStateObj ? 'Select a city' : 'Select state first')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: errors.city ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                      '&:hover fieldset': {
                        borderColor: errors.city ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: errors.city ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                      },
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Pincode"
              value={guestPincode || ''}
              onChange={(e) => handlePincodeChange(e.target.value)}
              error={!!errors.pincode}
              helperText={errors.pincode}
              inputProps={{
                maxLength: 10,
                style: { textTransform: 'uppercase' },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: errors.pincode ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                  },
                  '&:hover fieldset': {
                    borderColor: errors.pincode ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: errors.pincode ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                  },
                },
              }}
            />
          </Grid>

          {isWalkIn ? (
            <>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: isDark ? '#ddd' : 'inherit',
                  }}
                >
                  Purpose of stay{' '}
                  <Typography component="span" color="error" sx={{ fontWeight: 600 }}>
                    *
                  </Typography>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {purposeOfStayOptions.map((opt) => {
                    const selected = purposeOfStay === opt.value;
                    return (
                      <Chip
                        key={opt.value}
                        label={opt.label}
                        onClick={() => handlePurposeChipClick(opt.value)}
                        variant={selected ? 'filled' : 'outlined'}
                        sx={{
                          cursor: 'pointer',
                          ...(selected
                            ? {
                                backgroundColor: THEME_COLORS.PRIMARY,
                                color: THEME_COLORS.PRIMARY_DARK,
                                borderColor: THEME_COLORS.PRIMARY,
                                '&:hover': {
                                  backgroundColor: THEME_COLORS.PRIMARY_HOVER,
                                },
                              }
                            : {
                                borderColor: errors.purposeOfStay
                                  ? theme.palette.error.main
                                  : CUSTOM_BORDER_COLOR,
                                color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
                                '&:hover': {
                                  borderColor: THEME_COLORS.PRIMARY,
                                  backgroundColor: alpha(THEME_COLORS.PRIMARY, isDark ? 0.14 : 0.1),
                                },
                              }),
                        }}
                      />
                    );
                  })}
                </Box>
                {errors.purposeOfStay ? (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', ml: 0.25 }}>
                    {errors.purposeOfStay}
                  </Typography>
                ) : null}
              </Grid>
              {purposeOfStay === 'other' ? (
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    required
                    label="Describe purpose"
                    value={purposeOfStayNote || ''}
                    onChange={(e) => {
                      if (onPurposeOfStayNoteChange) {
                        onPurposeOfStayNoteChange(e.target.value);
                      }
                      const v = e.target.value;
                      if (v && v.trim()) {
                        setErrors((prev) => ({ ...prev, purposeOfStayNote: '' }));
                      }
                    }}
                    error={!!errors.purposeOfStayNote}
                    helperText={errors.purposeOfStayNote}
                    multiline
                    minRows={2}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: errors.purposeOfStayNote
                            ? theme.palette.error.main
                            : CUSTOM_BORDER_COLOR,
                        },
                        '&:hover fieldset': {
                          borderColor: errors.purposeOfStayNote
                            ? theme.palette.error.main
                            : CUSTOM_BORDER_COLOR,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: errors.purposeOfStayNote
                            ? theme.palette.error.main
                            : CUSTOM_BORDER_COLOR,
                        },
                      },
                    }}
                  />
                </Grid>
              ) : null}
            </>
          ) : null}

          {/* Row 9: Document type + ID number */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={isForeignGuest}>
              <InputLabel>Select Document type</InputLabel>
              <Select
                value={uploadDocumentType || ''}
                label="Select Document type"
                disabled={isForeignGuest}
                MenuProps={drawerOverlays?.selectMenuProps}
                onChange={(e) => {
                  if (isForeignGuest) return;
                  if (onUploadDocumentChange) {
                    const docType = e.target.value;
                    onUploadDocumentChange(docType);
                    if (docType === WALK_IN_NO_DOCUMENT_VALUE) {
                      if (onDocumentIdNumberChange) onDocumentIdNumberChange('');
                      // "No Document": clear upload state/errors so users can continue.
                      if (onPassportFrontIdChange) onPassportFrontIdChange('');
                      if (onPassportBackIdChange) onPassportBackIdChange('');
                      setErrors(prev => ({ ...prev, documentNumber: '', documentUpload: '' }));
                      return;
                    }
                    // Validate document number when document type changes
                    const documentNumberError = validateDocumentIdNumber(
                      docType,
                      documentIdNumber
                    );
                    if (docType) {
                      setErrors(prev => ({
                        ...prev,
                        documentNumber: documentNumberError || '',
                      }));
                    } else {
                      setErrors(prev => ({ ...prev, documentNumber: '' }));
                    }
                  }
                }}
                onBlur={() => {
                  // Validate document number on blur if document type is selected
                  if (effectiveDocumentType) {
                    const documentNumberError = validateDocumentIdNumber(effectiveDocumentType, documentIdNumber);
                    setErrors(prev => ({
                      ...prev,
                      documentNumber: documentNumberError || '',
                    }));
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: CUSTOM_BORDER_COLOR,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: CUSTOM_BORDER_COLOR,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: CUSTOM_BORDER_COLOR,
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select Document type</em>
                </MenuItem>
                {documentTypeOptions.map((doc) => (
                  <MenuItem key={doc.value} value={doc.value}>
                    {doc.label}
                  </MenuItem>
                ))}
              </Select>
              {isForeignGuest ? (
                <Typography variant="caption" sx={{ mt: 0.5, ml: 1.5, color: 'text.secondary' }}>
                  Set from Foreign Guest Details
                </Typography>
              ) : null}
            </FormControl>
          </Grid>
          {shouldShowDocumentNumberField ? (
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                required={!!effectiveDocumentType}
                label={`${getDocumentTypeLabel()} Number`}
                placeholder={getDocumentNumberPlaceholder()}
                value={normalizedDocumentIdValue}
                disabled={isForeignGuest}
                onChange={(e) => {
                  if (isForeignGuest) return;
                  const value = normalizeDocumentIdInput(effectiveDocumentType, e.target.value || '');
                  if (onDocumentIdNumberChange) {
                    onDocumentIdNumberChange(value);
                  }
                  const error = validateDocumentIdNumber(effectiveDocumentType, value);
                  setErrors(prev => ({ ...prev, documentNumber: error }));
                }}
                onBlur={() => {
                  if (isForeignGuest) return;
                  const error = validateDocumentIdNumber(effectiveDocumentType, normalizedDocumentIdValue);
                  setErrors(prev => ({ ...prev, documentNumber: error }));
                }}
                error={!!errors.documentNumber}
                helperText={
                  errors.documentNumber ||
                  (isForeignGuest ? 'Set from Foreign Guest Details' : '')
                }
                InputProps={{
                  readOnly: isForeignGuest,
                }}
                inputProps={{
                  maxLength: isAadhaarSelected ? 14 : undefined,
                  inputMode: isAadhaarSelected ? 'numeric' : undefined,
                  pattern: isAadhaarSelected ? '[0-9\\-]*' : undefined,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: errors.documentNumber ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                    },
                    '&:hover fieldset': {
                      borderColor: errors.documentNumber ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: errors.documentNumber ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                    },
                  },
                }}
              />
            </Grid>
          ) : null}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, height: '100%', alignItems: 'center' }}>
              <FormControlLabel
                label="Camera upload"
                labelPlacement="start"
                control={
                  <Switch
                    checked={cameraUpload}
                    onChange={(e) => setCameraUpload(e.target.checked)}
                    color="primary"
                  />
                }
              />
            </Box>
          </Grid>
          {/* Row 10: Document Upload - Front and Back */}
          {errors.documentUpload ? (
            <Grid item xs={12}>
              <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                {errors.documentUpload}
              </Typography>
            </Grid>
          ) : null}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {getFrontDocumentLabel()}
              {effectiveDocumentType ? ' *' : ''}
            </Typography>
            <Paper
              sx={{
                p: 2,
                border: `2px dashed ${
                  errors.documentUpload ? theme.palette.error.main : CUSTOM_BORDER_COLOR
                }`,
                borderRadius: 2,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: errors.passportFront
                  ? (isDark ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.04)')
                  : (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'),
                '&:hover': {
                  backgroundColor: errors.passportFront
                    ? (isDark ? 'rgba(244, 67, 54, 0.12)' : 'rgba(244, 67, 54, 0.06)')
                    : (isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'),
                },
              }}
              onClick={() => handleUploadClick('front')}
            >
              {passportFrontId ? (
                <Box>
                  <img
                    src={passportFrontId}
                    alt={`${getDocumentTypeLabel()} Front`}
                    style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: 4 }}
                  />
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDocumentRemove('front');
                    }}
                    sx={{ mt: 1 }}
                  >
                    Remove
                  </Button>
                </Box>
              ) : (
                <Box>
                  {cameraUpload ? (
                    <CameraAltIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }} />
                  ) : (
                    <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }} />
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {cameraUpload ? 'Click to capture from camera' : 'Click to upload or drag and drop'}
                  </Typography>
                </Box>
              )}
              <input
                ref={fileInputFrontRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => handleFileUpload(e, 'front')}
              />
            </Paper>
            {errors.passportFront && (
              <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                {errors.passportFront}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {getBackDocumentLabel()}
              {effectiveDocumentType ? ' *' : ''}
            </Typography>
            <Paper
              sx={{
                p: 2,
                border: `2px dashed ${
                  errors.documentUpload ? theme.palette.error.main : CUSTOM_BORDER_COLOR
                }`,
                borderRadius: 2,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: errors.passportBack
                  ? (isDark ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.04)')
                  : (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'),
                '&:hover': {
                  backgroundColor: errors.passportBack
                    ? (isDark ? 'rgba(244, 67, 54, 0.12)' : 'rgba(244, 67, 54, 0.06)')
                    : (isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'),
                },
              }}
              onClick={() => handleUploadClick('back')}
            >
              {passportBackId ? (
                <Box>
                  <img
                    src={passportBackId}
                    alt={`${getDocumentTypeLabel()} Back`}
                    style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: 4 }}
                  />
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDocumentRemove('back');
                    }}
                    sx={{ mt: 1 }}
                  >
                    Remove
                  </Button>
                </Box>
              ) : (
                <Box>
                  {cameraUpload ? (
                    <CameraAltIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }} />
                  ) : (
                    <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }} />
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {cameraUpload ? 'Click to capture from camera' : 'Click to upload or drag and drop'}
                  </Typography>
                </Box>
              )}
              <input
                ref={fileInputBackRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => handleFileUpload(e, 'back')}
              />
            </Paper>
            {errors.passportBack && (
              <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                {errors.passportBack}
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* Camera Modal */}
        {cameraOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
            onClick={(e) => {
              // Close on backdrop click
              if (e.target === e.currentTarget) {
                stopCamera();
              }
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '800px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  mb: 2,
                  fontWeight: 600,
                }}
              >
                {cameraType === 'front' ? 'Capture Passport Front' : 'Capture Passport Back'}
              </Typography>

              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '600px',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: 'black',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  mt: 3,
                  width: '100%',
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={capturePhoto}
                  size="large"
                  sx={{
                    minWidth: 150,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  Capture Photo
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={stopCamera}
                  size="large"
                  sx={{
                    minWidth: 150,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <ForeignGuestDetailsDrawer
        open={foreignGuestDrawerOpen}
        onClose={() => setForeignGuestDrawerOpen(false)}
        hotelCountry={hotelCountry}
        guestCountry={guestCountry}
        roomNumber={foreignGuestRoomNumber}
        checkInDate={foreignGuestCheckInDate}
        initialDetails={foreignGuestDetails}
        onSave={(nextDetails) => {
          onForeignGuestDetailsChange?.(nextDetails);
          if (nextDetails.passportNumber) {
            onUploadDocumentChange?.('passport');
            onDocumentIdNumberChange?.(String(nextDetails.passportNumber).trim());
          }
          setErrors((prev) => ({ ...prev, foreignGuest: '' }));
          setForeignGuestDrawerOpen(false);
        }}
      />

      <Snackbar
        open={cameraPermissionSnackbar.open}
        autoHideDuration={9000}
        onClose={() => setCameraPermissionSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCameraPermissionSnackbar((s) => ({ ...s, open: false }))}
          severity="warning"
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
        >
          {cameraPermissionSnackbar.message}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default GuestDetailsStep;
